import { makeAutoObservable, runInAction } from "mobx";
import { fromResponseState } from "./POrderSummaryState";
import { fromResponseLoyalty, } from "./POrderSummaryLoyalty";
import { POrderInfo } from "./POrderInfo";
import { trackEventUnique } from "../../tracking/trackingService";
const POLLING_INTERVAL = 15000;
export class POrderSummary {
    constructor(params) {
        this.localizationProvider = params.localizationProvider;
        this.orderSummaryService = params.orderSummaryService;
        this.cartStorageManager = params.cartStorageManager;
        this.redirectToRestaurant = params.redirectToRestaurant;
        this.orderUuid = params.orderUuid;
        this.isPollingActive = false;
        this.timeout = null;
        this._state = { type: "Loading", isFinal: false };
        this._userLoyalty = { type: "Hidden", showModal: false };
        this._orderInfo = null;
        this._isError = false;
        makeAutoObservable(this);
    }
    get isError() {
        return this._isError;
    }
    setError(isError) {
        this._isError = isError;
    }
    setState(orderSummaryResponse) {
        // Assert we are not leaving final state
        if (this._state.isFinal) {
            throw new Error(`${this._state.type} is final state, refusing to transition`);
        }
        this._state = fromResponseState({
            cartStorageManager: this.cartStorageManager,
            orderSummaryResponse,
            redirectToRestaurant: this.redirectToRestaurant,
        });
        if (this._state.isFinal) {
            this.stopPolling();
        }
    }
    get state() {
        return this._state;
    }
    setOrderInfo(orderInfo) {
        const localizationProvider = this.localizationProvider;
        this._orderInfo = new POrderInfo({ localizationProvider, orderInfo });
    }
    get orderInfo() {
        return this._orderInfo;
    }
    setUserLoyalty(state, userLoyalty) {
        this._userLoyalty = fromResponseLoyalty(state, userLoyalty);
    }
    get userLoyalty() {
        return this._userLoyalty;
    }
    initialize() {
        this.cartStorageManager.storePreviousOrderCart();
        this.cartStorageManager.clearCart();
        this.startPolling();
    }
    startPolling() {
        if (this.isPollingActive === true) {
            throw new Error("Double polling initialization error");
        }
        this.isPollingActive = true;
        this.fetchOrderSummary();
    }
    mkTrackingItem(item) {
        return {
            name: item.name,
            price: item.unitPrice,
            quantity: item.quantity,
            category: item.promotion ? "Promotional" : "NonPromotional",
            list: null,
        };
    }
    mkTrackingItemFromDiscount(item) {
        return {
            name: item.name,
            price: -item.value,
            quantity: 1,
            category: "Promotional",
            list: null,
        };
    }
    fetchOrderSummary() {
        this.orderSummaryService
            .getOrderSummary(this.orderUuid)
            .then((response) => {
            var _a;
            runInAction(() => {
                this.setError(false);
                this.setState(response);
                this.setOrderInfo(response.orderInfo);
                this.setUserLoyalty(response.state, response.userLoyalty);
            });
            const coupon = response.orderInfo.coupons[0];
            trackEventUnique(this.orderUuid, {
                tag: "Purchase",
                contents: {
                    affiliation: (_a = response.siteName) !== null && _a !== void 0 ? _a : "",
                    shipping: response.orderInfo.deliveryCost,
                    transactionId: this.orderUuid,
                    total: response.orderInfo.total,
                    items: response.orderInfo.items
                        .map((item) => this.mkTrackingItem(item))
                        .concat(response.orderInfo.discounts.map((discount) => this.mkTrackingItemFromDiscount(discount))),
                    coupon: coupon === null || coupon === void 0 ? void 0 : coupon.code,
                },
            });
        })
            .catch(() => this.setError(true))
            .finally(() => {
            if (this.isPollingActive)
                this.fetchOrderSummaryWithTimeout();
        });
    }
    fetchOrderSummaryWithTimeout() {
        this.timeout = setTimeout(() => this.fetchOrderSummary(), POLLING_INTERVAL);
    }
    stopPolling() {
        this.isPollingActive = false;
        this.clearTimeout();
    }
    dispose() {
        this.stopPolling();
    }
    clearTimeout() {
        if (this.timeout !== null) {
            clearTimeout(this.timeout);
            this.timeout = null;
        }
    }
}
